"use client";

import type * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import type { ButtonProps } from "@/app/_ui/components/core/Button";
import { Button } from "@/app/_ui/components/core/Button";
import { Dialog } from "@/app/_ui/components/core/DialogRedesign";
import { cn } from "@ui/lib/utils";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";

const TRANSITION = { duration: 0.25, ease: "easeOut" as const };
const AUTO_CLOSE_DELAY_MS = 3000;

export interface FormDialogButtonProps {
  /** Button label that opens the dialog */
  buttonLabel: string;
  /** Dialog title */
  title: string;
  /** Dialog description below the title */
  description: string;
  /** Success screen title */
  successTitle: string;
  /** Success screen message */
  successMessage: string;
  /** Success screen button label */
  successButton: string;
  /** Render the form. Receives onSuccess and onCancel callbacks. */
  renderForm: (props: { onSuccess: () => void; onCancel: () => void }) => React.ReactNode;
  onTriggerClick?: () => void;
  buttonVariant?: ButtonProps["variant"];
  buttonSize?: ButtonProps["size"];
  contentClassName?: string;
}

export function FormDialogButton({
  buttonLabel,
  title,
  description,
  successTitle,
  successMessage,
  successButton,
  renderForm,
  onTriggerClick,
  buttonVariant = "primary",
  buttonSize = "lg",
  contentClassName,
}: FormDialogButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearAutoCloseTimer = useCallback(() => {
    if (autoCloseTimerRef.current === null) {
      return;
    }

    clearTimeout(autoCloseTimerRef.current);
    autoCloseTimerRef.current = null;
  }, []);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      clearAutoCloseTimer();
      setIsOpen(nextOpen);

      if (nextOpen) {
        setSucceeded(false);
      }
    },
    [clearAutoCloseTimer],
  );

  const closeDialog = useCallback(() => {
    handleOpenChange(false);
  }, [handleOpenChange]);

  useEffect(() => clearAutoCloseTimer, [clearAutoCloseTimer]);

  const handleSuccess = useCallback(() => {
    clearAutoCloseTimer();
    setSucceeded(true);
    autoCloseTimerRef.current = setTimeout(closeDialog, AUTO_CLOSE_DELAY_MS);
  }, [clearAutoCloseTimer, closeDialog]);

  const dialogTitle = succeeded ? successTitle : title;

  return (
    <>
      <Button
        onClick={() => {
          onTriggerClick?.();
          handleOpenChange(true);
        }}
        size={buttonSize}
        type='button'
        variant={buttonVariant}
      >
        {buttonLabel}
      </Button>
      <Dialog isDismissable isOpen={isOpen} onOpenChange={handleOpenChange} title={dialogTitle}>
        <div className={cn("tw-mt-6", contentClassName)}>
          <AnimatePresence mode='wait'>
            {!succeeded ? (
              <m.div key='form' exit={{ opacity: 0, y: -8 }} transition={TRANSITION}>
                <p className='tw-type-sm tw-text-gray-500 tw-text-pretty'>{description}</p>
                <div className='tw-mt-7'>{renderForm({ onSuccess: handleSuccess, onCancel: closeDialog })}</div>
              </m.div>
            ) : (
              <m.div
                key='success'
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={TRANSITION}
                className='tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16 tw-text-center'
              >
                <p className='tw-type-sm tw-text-gray-500 tw-mb-8 tw-max-w-sm'>{successMessage}</p>
                <Button variant='primary' size='lg' onClick={closeDialog} type='button'>
                  {successButton}
                </Button>
              </m.div>
            )}
          </AnimatePresence>
        </div>
      </Dialog>
    </>
  );
}
